import { z } from 'zod';
import { LanguageSchema } from '../common/language.js';
import { NumeralsSchema } from '../common/numerals.js';
import { EntitlementFlagSchema } from '../permissions/catalogue.js';
import { ResponsibilityLevelSchema } from '../responsibilities/levels.js';
// Response schemas for /api/auth/* routes.
// Fastify validates outgoing payloads against these via the type provider —
// handler-vs-schema drift fails at serialization, in dev/test (CONVENTIONS
// §Schemas + ADR-009).
// ── shared building blocks (reusable for /me + login-MFA flows) ────────────
// User identity surface delivered by /me. `is_sysadmin` lives here (NOT
// repeated per project — ADR-003 §Permission shape locks the cache shape but
// the wire shape is leaner). `must_change_password` short-circuits the
// frontend resolver before /me's other fields matter.
export const UserSchema = z
    .object({
    id: z.string().max(16),
    username: z.string().max(100),
    email: z.string().max(128).nullable(),
    first_name: z.string().max(64),
    last_name: z.string().max(64),
    must_change_password: z.boolean(),
    is_sysadmin: z.boolean(),
    theme_mode: z.enum(['light', 'dark', 'system']),
    // `language` = the user's real language (renamed from `default_language` —
    // ADR-030; on a user it's the value, not a default). `numerals` = the digit
    // system the formatter reads (lib/format.ts). Settable UI is future work.
    language: LanguageSchema,
    numerals: NumeralsSchema,
    // The person's zone (ADR-082 §2 · §3) — their own screens' clock, "your time" beside a project's in a
    // peek, the drift strip's account side; set in the profile (PATCH /auth/me/profile).
    timezone_iana: z.string().max(64),
    last_project_id: z.string().max(16).nullable(),
})
    .strict();
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
// Project entry in /me's availableProjects list. Includes suspended +
// terminated; frontend access-gate handles routing per ADR-016.
export const AvailableProjectSchema = z
    .object({
    id: z.string().max(16),
    // The display label: COALESCE(display_name, name) from /me. Sized to
    // display_name's column width (64), not the short `name` (32).
    name: z.string().max(64),
    status: z.enum(['active', 'suspended', 'terminated']),
    // Brand pair — the switcher swatch + select-project card edge, AND full
    // document/page branding for projects `GET /projects/:id` can't serve
    // (suspended / expired-trial → 403): the /project-blocked page + its favicon
    // and tab title resolve brand from here. The two travel together (the ink is
    // meaningless without its fill), so both ship on every available project.
    color_primary: z.string().regex(HEX_COLOR),
    color_on_primary: z.string().regex(HEX_COLOR),
    // Consumers: the select-project picker (type tag + trial badge) AND the
    // frontend reachability mirror (resolveLanding / picker filter / blocked
    // page) — which re-derives the requireProjectAccess predicate. The backend
    // gate stays authoritative; these are advisory-for-routing (like the F7
    // permission mirror).
    type: z.enum(['esco', 'embedded', 'demo']),
    is_trial: z.boolean(),
    trial_expires_at: z.string().datetime().nullable(),
    // The project's size at a glance — its ACTIVE sites, counted whole (the
    // project's count, not the viewer's scoped reach): the second line of a
    // project row in the switcher and the chooser ("ESCO · 42 active sites").
    active_site_count: z.number().int().nonnegative(),
})
    .strict();
// Per-project permissions entry. `is_sysadmin` is at user-level (UserSchema),
// not repeated here — the render gate short-circuits on user.is_sysadmin /
// is_project_admin before consulting the list.
export const ProjectPermissionsSchema = z
    .object({
    is_project_admin: z.boolean(),
    scope_mode: z.enum(['all', 'assigned']).nullable(),
    // Includes-expanded, entitlement-filtered capability keys held in this project
    // (ADR-025 — discrete keys, no values; `write` ⊇ `read` already resolved;
    // off-module keys already dropped — ADR-044). The frontend gate checks
    // membership: hasPermission(perms, key) = perms.includes(key).
    permissions: z.array(z.string()),
    // The project's ON optional-module flags (ADR-044). Member-facing so the nav +
    // useCan gate hide off-module surfaces — for EVERYONE, since useCan mirrors
    // can()'s entitlement gate (admins included). This is the entitlement ON-set's
    // wire home: it rides the session/permission payload, NOT the project metadata
    // read (`ProjectSchema` stays clean — field-exposure rule). Reveals no more
    // than the nav already does (an invoicing item appears ⇒ invoicing is on).
    entitlements: z.array(EntitlementFlagSchema),
    // The responsibility LEVELS this actor may ASSIGN at in this project — the
    // inline level-set narrowing of `users:manage`'s accountability action
    // (ADR-026 §E). Admins (sysadmin / project_admin) carry the full
    // RESPONSIBILITY_LEVELS set; a role-holder carries its role's
    // `responsibility_levels`; a member with no assigning role carries []. The
    // frontend gates the assign/manage affordance on `users:manage` AND (level in
    // this set); the backend re-checks at the route (core/accountability
    // assertCanAssignAtLevel). Rides the permission payload, not the project
    // metadata read (field-exposure rule).
    assignableResponsibilityLevels: z.array(ResponsibilityLevelSchema),
    // What this person has of their OWN in the Work area with no key — consumer: the Work
    // area's menu gates (a menu entry shows when you hold its key or have something in it).
    // `tickets` = on at least one ticket (the hub's participation arm); `teams` = on at
    // least one team (the Teams list's membership arm). Asked on `/me` alone, never on the
    // per-request permission entry; false where the field-force module is off.
    participates: z.object({ tickets: z.boolean(), teams: z.boolean() }).strict(),
})
    .strict();
// ── GET /api/auth/me ───────────────────────────────────────────────────────
export const MeResponseSchema = z
    .object({
    user: UserSchema,
    availableProjects: z.array(AvailableProjectSchema),
    permissions: z.record(z.string().max(16), ProjectPermissionsSchema),
})
    .strict();
// ── POST /api/auth/login ──────────────────────────────────────────────────
// Polymorphic: success vs MFA-required.
//   - Success: Set-Cookie + minimal body so the FE can short-circuit the
//     must_change_password routing decision before useQuery(['me']) arrives.
//   - MFA-required: no cookie set yet (MFA session-rotation per ADR-003);
//     client posts code + mfa_token to /api/auth/mfa/verify.
export const LoginSuccessResponseSchema = z
    .object({
    mfa_required: z.literal(false),
    must_change_password: z.boolean(),
})
    .strict();
export const LoginMfaRequiredResponseSchema = z
    .object({
    mfa_required: z.literal(true),
    mfa_token: z.string(),
})
    .strict();
export const LoginResponseSchema = z.discriminatedUnion('mfa_required', [
    LoginSuccessResponseSchema,
    LoginMfaRequiredResponseSchema,
]);
// ── POST /api/auth/mfa/verify ─────────────────────────────────────────────
// Same shape as login success (cookie set; FE follows up with /me).
export const MfaVerifyResponseSchema = LoginSuccessResponseSchema;
// ── POST /api/auth/mfa/enroll ─────────────────────────────────────────────
// Server generates secret + QR; nothing persisted yet (pending until verify).
export const MfaEnrollResponseSchema = z
    .object({
    secret: z.string(), // base32 secret shown to the user once
    otpauth_url: z.string(), // otpauth://totp/... — for QR or manual entry
})
    .strict();
// ── 204 No Content responses ──────────────────────────────────────────────
// POST /api/auth/mfa/enroll/verify, POST /api/auth/mfa/disable,
// POST /api/auth/logout, POST /api/auth/change-password,
// PATCH /api/auth/me/profile — all return 204 with no body. No schema needed.
